import { TableName } from '@openx/components-table/Table';
import { CriteriaDimension, SortDirection } from '@openx/types/tableCriteria';
import { abbreviateNumericValue } from '@openx/utils/lib/abbreviateNumericValue';
import { clientSideCriteriaProcessing } from '@openx/utils/lib/clientSideCriteriaProcessing';
import { useMemo } from 'react';
import { useDealInsightsContext } from '../../../../../context/DealInsightsContext';
import { sortAdDurationDataByLabel } from '../utils/adDurationLabels';
import type { DealInsightsData } from './useDeliveryByAdDurationTableColumns';

export const useDeliveryByAdDurationTableData = () => {
  const { dealInsights, criteria, handleCriteriaChange } = useDealInsightsContext();
  const { data, chartsLoadingState } = dealInsights;

  const transformedData = useMemo<DealInsightsData[]>(() => {
    return (
      data?.delivery_by_ad_duration.objects.map(item => {
        const sovPercentage = item.sov !== null ? item.sov * 100 : null;
        const ctrPercentage = item.ctr !== null ? item.ctr * 100 : null;
        const viewabilityPercentage =
          item.viewability !== null && item.viewability !== undefined ? item.viewability * 100 : null;

        return {
          ...item,
          spend_raw: item.spend,
          sov_raw: item.sov,
          ecpm_raw: item.ecpm,
          ctr_raw: item.ctr,
          viewability_raw: item.viewability ?? null,
          impressions_raw: item.impressions,
          ecpm: abbreviateNumericValue(item.ecpm, { allowZero: true, nullValue: 'N/A' }),
          sov: abbreviateNumericValue(sovPercentage, { allowZero: true, nullValue: 'N/A' }),
          spend: abbreviateNumericValue(item.spend, { allowZero: true, nullValue: 'N/A' }),
          ctr: abbreviateNumericValue(ctrPercentage, { allowZero: true, nullValue: 'N/A' }),
          viewability: abbreviateNumericValue(viewabilityPercentage, { allowZero: true, nullValue: 'N/A' }),
          impressions: abbreviateNumericValue(item.impressions, { allowZero: true, nullValue: 'N/A' }),
        };
      }) ?? []
    );
  }, [data?.delivery_by_ad_duration.objects]);

  const sortedData = useMemo<DealInsightsData[]>(() => {
    if (!criteria?.[TableName.DEAL_INSIGHTS_DELIVERY_BY_AD_DURATION]) {
      return transformedData;
    }

    const tableCriteria = criteria[TableName.DEAL_INSIGHTS_DELIVERY_BY_AD_DURATION];
    const sortCriteria = tableCriteria?.[CriteriaDimension.SORT];

    if (!sortCriteria?.column) {
      return transformedData;
    }

    if (sortCriteria.column === 'ad_duration') {
      return sortAdDurationDataByLabel(transformedData, sortCriteria.direction);
    }

    const sortKey = `${sortCriteria.column}_raw` as keyof DealInsightsData;
    return [...transformedData].sort((a, b) => {
      const valueA = a[sortKey] as number;
      const valueB = b[sortKey] as number;

      const comparison = valueA - valueB;
      return sortCriteria.direction === SortDirection.DESC ? -comparison : comparison;
    });
  }, [transformedData, criteria?.[TableName.DEAL_INSIGHTS_DELIVERY_BY_AD_DURATION]]);

  const processedData = useMemo(() => {
    const tableCriteria = criteria?.[TableName.DEAL_INSIGHTS_DELIVERY_BY_AD_DURATION];
    if (!tableCriteria) {
      return sortedData;
    }

    return clientSideCriteriaProcessing(sortedData, tableCriteria, {
      ignoreDimensions: [CriteriaDimension.SORT, CriteriaDimension.FILTERS],
      onCriteriaAdjust: adjustedCriteria => {
        if (adjustedCriteria.pagination) {
          handleCriteriaChange(
            {
              dimension: CriteriaDimension.PAGINATION,
              value: adjustedCriteria.pagination,
            },
            TableName.DEAL_INSIGHTS_DELIVERY_BY_AD_DURATION
          );
        }
      },
    });
  }, [sortedData, criteria?.[TableName.DEAL_INSIGHTS_DELIVERY_BY_AD_DURATION], handleCriteriaChange]);

  return {
    data: processedData,
    isLoading: !!chartsLoadingState.delivery_by_ad_duration,
  };
};
